import React, { useState, useEffect } from "react";
import "./PeopleList.css";

export default function PeopleList() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch all people from the database
  const fetchAllPeople = async () => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`/api/people/all`, {
        credentials: 'include', // Include cookies for better-auth session
      });
      
      if (response.ok) {
        const data = await response.json();
        setPeople(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch people');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Delete a person from the database
  const deletePerson = async (personId) => {
    if (!window.confirm('Are you sure you want to delete this person?')) {
      return;
    }

    try {
      const response = await fetch(`/api/people/${personId}`, {
        method: 'DELETE',
        credentials: 'include', // Include cookies for better-auth session
      });

      if (response.ok) {
        // Remove the person from the local state
        setPeople(people.filter(person => person._id !== personId));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete person');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  useEffect(() => {
    fetchAllPeople();
  }, []);

  if (loading) {
    return (
      <div className="people-list-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading people...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="people-list-container">
      <div className="people-list-header">
        <h2>All People ({people.length})</h2>
        <button onClick={fetchAllPeople} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {people.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No people found</h3>
          <p>Start by adding some people to your directory or search for authors from published papers.</p>
        </div>
      ) : (
        <div className="people-grid">
          {people.map((person) => {
            // Extract profile link from summary if it exists
            const profileLinkMatch = person.summary?.match(/Profile: (https?:\/\/[^\s]+)/);
            const profileLink = profileLinkMatch ? profileLinkMatch[1] : null;
            
            // Extract title from summary if it exists  
            const titleMatch = person.summary?.match(/^(Prof\.|Dr\.?|Professor|Doctor)/);
            const title = titleMatch ? titleMatch[1] : null;
            
            return (
              <div key={person._id} className="person-card">
                <div className="person-avatar">
                  {person.name.charAt(0).toUpperCase()}
                </div>
                
                <div className="person-info">
                  <h3 className="person-name">
                    {title && <span className="person-title">{title} </span>}
                    {person.name}
                  </h3>
                  
                  {person.email && person.email.trim() && (
                    <p className="person-email">
                      <span className="info-icon">📧</span>
                      <a href={`mailto:${person.email}`}>{person.email}</a>
                    </p>
                  )}
                  
                  {person.department && (
                    <p className="person-department">
                      <span className="info-icon">🏫</span>
                      {person.department}
                    </p>
                  )}
                  
                  {person.affiliation && (
                    <p className="person-affiliation">
                      <span className="info-icon">🏢</span>
                      {person.affiliation}
                    </p>
                  )}
                  
                  {profileLink && (
                    <p className="person-profile">
                      <span className="info-icon">🔗</span>
                      <a 
                        href={profileLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="profile-link"
                      >
                        View Profile
                      </a>
                    </p>
                  )}
                  
                  {person.skills && person.skills.length > 0 && (
                    <div className="person-skills">
                      <span className="info-icon">🔬</span>
                      <div className="skills-tags">
                        {person.skills.slice(0, 3).map((skill, index) => (
                          <span key={index} className="skill-tag">
                            {skill}
                          </span>
                        ))}
                        {person.skills.length > 3 && (
                          <span className="skill-tag more">
                            +{person.skills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {person.paper && (
                  <p className="person-paper">
                    <span className="info-icon">📄</span>
                    {person.paper}
                  </p>
                )}
                
                {person.doi && (
                  <p className="person-doi">
                    <span className="info-icon">🔗</span>
                    <a 
                      href={`https://doi.org/${person.doi}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="doi-link"
                    >
                      {person.doi}
                    </a>
                  </p>
                )}
                
                {person.source && (
                  <div className="person-source">
                    <span className={`source-badge ${person.source === 'manual' ? 'manual' : 'paper'}`}>
                      {person.source === 'manual' ? '✋ Added Manually' : '📚 From Paper'}
                    </span>
                  </div>
                )}
                </div>
                
                <div className="person-actions">
                  <button 
                    onClick={() => deletePerson(person._id)}
                    className="delete-button"
                    title="Delete person"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}